import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import {
  Box,
  Typography,
  Grid,
  Card,
  CardContent,
  TextField,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Chip,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Alert,
  CircularProgress,
  Avatar,
} from '@mui/material';
import {
  Search,
  Edit,
  Delete,
  Add,
  Store,
  Visibility,
  VisibilityOff,
  List,
} from '@mui/icons-material';
import { allegroApiService } from '../shared/services/allegroApi';
import { useShops } from '../shared/contexts/ShopsContext';
import { containsString } from '../shared/utils/stringUtils';

const Shops = () => {
  const [filteredShops, setFilteredShops] = useState([]);
  const { shops, loading, error, refreshShops } = useShops();
  const [searchTerm, setSearchTerm] = useState('');
  const [addDialog, setAddDialog] = useState({ open: false });
  const [editDialog, setEditDialog] = useState({ open: false, shop: null });
  const [deleteDialog, setDeleteDialog] = useState({ open: false, shop: null });
  const [showSecrets, setShowSecrets] = useState({});
  const [newShop, setNewShop] = useState({
    name: '',
    clientId: '',
    clientSecret: '',
    redirectUri: '',
  });
  const [editForm, setEditForm] = useState({
    name: '',
    clientId: '',
    clientSecret: '',
    redirectUri: '',
  });

  useEffect(() => {
    filterShops();
  }, [shops, searchTerm]);

  const handleAddShop = async () => {
    try {
      await allegroApiService.addShop(newShop);
      setAddDialog({ open: false });
      setNewShop({ name: '', clientId: '', clientSecret: '', redirectUri: '' });
      refreshShops();
    } catch (err) {
      console.error('Błąd dodawania sklepu:', err);
    }
  };

  const handleEditShop = (shop) => {
    setEditForm({
      name: shop.name,
      clientId: shop.clientId,
      clientSecret: shop.clientSecret,
      redirectUri: shop.redirectUri,
    });
    setEditDialog({ open: true, shop });
  };

  const handleSaveEdit = async () => {
    try {
      await allegroApiService.updateShop(editDialog.shop.id, editForm);
      setEditDialog({ open: false, shop: null });
      refreshShops();
    } catch (err) {
      console.error('Błąd aktualizacji sklepu:', err);
    }
  };

  const handleDeleteShop = async () => {
    try {
      await allegroApiService.deleteShop(deleteDialog.shop.id);
      setDeleteDialog({ open: false, shop: null });
      refreshShops();
    } catch (err) {
      console.error('Błąd usuwania sklepu:', err);
    }
  };

  const filterShops = () => {
    let filtered = shops;

    if (searchTerm) {
      filtered = filtered.filter(shop => 
        containsString(shop.name, searchTerm) || containsString(shop.clientId, searchTerm)
      );
    }

    setFilteredShops(filtered);
  };

  const toggleSecretVisibility = (shopId) => {
    setShowSecrets(prev => ({
      ...prev,
      [shopId]: !prev[shopId]
    }));
  };

  const getTokenStatus = (shop) => {
    if (!shop.accessToken) return { label: 'Brak tokenu', color: 'error' };
    if (shop.expiresAt && Date.now() > shop.expiresAt) {
      return { label: 'Token wygasł', color: 'warning' };
    }
    return { label: 'Token aktywny', color: 'success' };
  };

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" height="50vh">
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box sx={{ textAlign: 'left' }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Typography variant="h4" gutterBottom sx={{ fontWeight: 700, color: 'text.primary' }}>
          Zarządzanie sklepami
        </Typography>
        <Button
          variant="contained"
          startIcon={<Add />}
          onClick={() => setAddDialog({ open: true })}
          sx={{
            borderRadius: 2,
            px: 3,
            py: 1,
            fontWeight: 600,
          }}
        >
          Dodaj sklep
        </Button>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 3, borderRadius: 2 }}>
          {error}
        </Alert>
      )}

      {/* Filtry i wyszukiwanie */}
      <Card sx={{ mb: 4, borderRadius: 3 }}>
        <CardContent sx={{ p: 3 }}>
          <Grid container spacing={3} alignItems="center">
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                placeholder="Wyszukaj sklepy..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                InputProps={{
                  startAdornment: <Search sx={{ mr: 1, color: 'text.secondary' }} />,
                }}
                sx={{
                  '& .MuiOutlinedInput-root': {
                    borderRadius: 2,
                  },
                }}
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <Typography variant="body2" color="text.secondary" sx={{ fontWeight: 500 }}>
                Znaleziono: {filteredShops.length} sklepów
              </Typography>
            </Grid>
          </Grid>
        </CardContent>
      </Card>

      {/* Tabela sklepów */}
      <TableContainer component={Paper} sx={{ borderRadius: 3, overflow: 'hidden' }}>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell sx={{ fontWeight: 600, backgroundColor: 'background.default' }}>Sklep</TableCell>
              <TableCell sx={{ fontWeight: 600, backgroundColor: 'background.default' }}>Client ID</TableCell>
              <TableCell sx={{ fontWeight: 600, backgroundColor: 'background.default' }}>Client Secret</TableCell>
              <TableCell sx={{ fontWeight: 600, backgroundColor: 'background.default' }}>Redirect URI</TableCell>
              <TableCell align="center" sx={{ fontWeight: 600, backgroundColor: 'background.default' }}>Status tokenu</TableCell>
              <TableCell align="center" sx={{ fontWeight: 600, backgroundColor: 'background.default' }}>Akcje</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {filteredShops.map((shop) => (
              <TableRow key={shop.id} sx={{ '&:hover': { backgroundColor: 'action.hover' } }}>
                <TableCell>
                  <Box sx={{ display: 'flex', alignItems: 'center' }}>
                    <Avatar sx={{ mr: 2, width: 40, height: 40, borderRadius: 2, bgcolor: 'primary.main' }}>
                      <Store />
                    </Avatar>
                    <Box>
                      <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>{shop.name}</Typography>
                      <Typography variant="caption" color="text.secondary">
                        ID: {shop.id}
                      </Typography>
                    </Box>
                  </Box>
                </TableCell>
                <TableCell>
                  <Box sx={{ display: 'flex', alignItems: 'center' }}>
                    <Typography variant="body2" sx={{ fontFamily: 'monospace' }}>
                      {shop.clientId}
                    </Typography>
                  </Box>
                </TableCell>
                <TableCell>
                  <Box sx={{ display: 'flex', alignItems: 'center' }}>
                    <Typography variant="body2" sx={{ fontFamily: 'monospace' }}>
                      {showSecrets[shop.id] ? shop.clientSecret : '••••••••••••••••'}
                    </Typography>
                    <IconButton
                      size="small"
                      onClick={() => toggleSecretVisibility(shop.id)}
                      sx={{ ml: 1 }}
                    >
                      {showSecrets[shop.id] ? <VisibilityOff /> : <Visibility />}
                    </IconButton>
                  </Box>
                </TableCell>
                <TableCell>
                  <Typography variant="body2" sx={{ fontFamily: 'monospace' }}>
                    {shop.redirectUri}
                  </Typography>
                </TableCell>
                <TableCell align="center">
                  <Chip
                    label={getTokenStatus(shop).label}
                    color={getTokenStatus(shop).color}
                    size="small"
                  />
                </TableCell>
                <TableCell align="center">
                  <IconButton
                    component={Link}
                    to={`/shop/${shop.id}`}
                    color="primary"
                    size="small"
                    sx={{ mr: 1 }}
                    title="Zobacz oferty"
                  >
                    <List />
                  </IconButton>
                  <IconButton
                    color="primary"
                    onClick={() => handleEditShop(shop)}
                    size="small"
                    sx={{ mr: 1 }}
                  >
                    <Edit />
                  </IconButton>
                  <IconButton
                    color="error"
                    onClick={() => setDeleteDialog({ open: true, shop })}
                    size="small"
                  >
                    <Delete />
                  </IconButton>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>

      {/* Dialog dodawania sklepu */}
      <Dialog open={addDialog.open} onClose={() => setAddDialog({ open: false })} maxWidth="sm" fullWidth>
        <DialogTitle sx={{ fontWeight: 600 }}>Dodaj nowy sklep</DialogTitle>
        <DialogContent>
          <Box sx={{ pt: 2 }}>
            <TextField
              fullWidth
              label="Nazwa sklepu"
              value={newShop.name}
              onChange={(e) => setNewShop({...newShop, name: e.target.value})}
              sx={{ mb: 2 }}
            />
            <TextField
              fullWidth
              label="Client ID"
              value={newShop.clientId}
              onChange={(e) => setNewShop({...newShop, clientId: e.target.value})}
              sx={{ mb: 2 }}
            />
            <TextField
              fullWidth
              label="Client Secret"
              type="password"
              value={newShop.clientSecret}
              onChange={(e) => setNewShop({...newShop, clientSecret: e.target.value})}
              sx={{ mb: 2 }}
            />
            <TextField
              fullWidth
              label="Redirect URI"
              value={newShop.redirectUri}
              onChange={(e) => setNewShop({...newShop, redirectUri: e.target.value})}
              sx={{ mb: 2 }}
            />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setAddDialog({ open: false })}>
            Anuluj
          </Button>
          <Button
            variant="contained"
            onClick={handleAddShop}
            disabled={!newShop.name || !newShop.clientId || !newShop.clientSecret || !newShop.redirectUri}
          >
            Dodaj sklep
          </Button>
        </DialogActions>
      </Dialog>

      {/* Dialog edycji sklepu */}
      <Dialog open={editDialog.open} onClose={() => setEditDialog({ open: false, shop: null })} maxWidth="sm" fullWidth>
        <DialogTitle sx={{ fontWeight: 600 }}>Edytuj sklep</DialogTitle>
        <DialogContent>
          <Box sx={{ pt: 2 }}>
            <TextField
              fullWidth
              label="Nazwa sklepu"
              value={editForm.name}
              onChange={(e) => setEditForm({...editForm, name: e.target.value})}
              sx={{ mb: 2 }}
            />
            <TextField
              fullWidth
              label="Client ID"
              value={editForm.clientId}
              onChange={(e) => setEditForm({...editForm, clientId: e.target.value})}
              sx={{ mb: 2 }}
            />
            <TextField
              fullWidth
              label="Client Secret"
              type="password"
              value={editForm.clientSecret}
              onChange={(e) => setEditForm({...editForm, clientSecret: e.target.value})}
              sx={{ mb: 2 }}
            />
            <TextField
              fullWidth
              label="Redirect URI"
              value={editForm.redirectUri}
              onChange={(e) => setEditForm({...editForm, redirectUri: e.target.value})}
              sx={{ mb: 2 }}
            />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setEditDialog({ open: false, shop: null })}>
            Anuluj
          </Button>
          <Button variant="contained" onClick={handleSaveEdit}>
            Zapisz zmiany
          </Button>
        </DialogActions>
      </Dialog>

      {/* Dialog potwierdzenia usuwania */}
      <Dialog open={deleteDialog.open} onClose={() => setDeleteDialog({ open: false, shop: null })}>
        <DialogTitle sx={{ fontWeight: 600 }}>Potwierdź usunięcie</DialogTitle>
        <DialogContent>
          <Typography>
            Czy na pewno chcesz usunąć sklep "{deleteDialog.shop?.name}"?
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteDialog({ open: false, shop: null })}>
            Anuluj
          </Button>
          <Button variant="contained" color="error" onClick={handleDeleteShop}>
            Usuń
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default Shops; 